import React, { useEffect, useState } from "react";
import { Card, Row, Col, Table, Typography, Divider, Modal, Form, InputNumber, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import { numberFormat, convertNumberToWords, formatToCompanyTimezone, formatDateOnly, numberFormatTax } from "../../../../../lib/functions";
import { purchaseRecordnWithDetailsApi } from "../../../../../apiservice/purchase";
import { getItemApi } from "../../../../../apiservice/accounts";
import { saveItemApi } from "../../../../../apiservice/inventory";

import "./voucher-mobile.css"
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import { useSelector } from "react-redux";

const { Title, Text } = Typography;

const PurchaseVoucherBody = ({ pur_id, institution, printMode = false, showStamp = false, showSignature = false, setVoucherNo, setAccName, setSupplierEmail }) => {
  const [data, setData] = useState(null);
  const [rateModalOpen, setRateModalOpen] = useState(false);
  const [rateModalLoading, setRateModalLoading] = useState(false);
  const [rateModalSaving, setRateModalSaving] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editingItemMeta, setEditingItemMeta] = useState(null);
  const [rateForm] = Form.useForm();
  const authInfo = useSelector(selectAuthInfo);
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const isIndiaGst = authInfo?.gst_enabled === "yes";
  const isPharmacy = authInfo?.company_type === "Pharmacy";

  const taxLabel = isIndia ? "GST" : "VAT";
  const isTatmeenUAE =
    authInfo?.is_tatmeen_enabled === "yes" &&
    authInfo?.country === "United Arab Emirates" && (authInfo?.subscription == 'PharmaERP_Pro' || authInfo?.subscription == 'PharmaERP_Standard' || authInfo?.subscription == 'PharmaERP_Basic');

  const isGSTDisabled =
    isIndia && authInfo?.gst_enabled == "no";


  useEffect(() => {
    if (!pur_id) return;
    purchaseRecordnWithDetailsApi({ pur_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) {
        const record = res.data[0];
        setData(record);

        // important guard for printMode
        if (!printMode && setVoucherNo) {
          setVoucherNo(record.pur_voucher_no);
          setAccName(record.acc_name);
        }

        if (record.email_address) {
          setSupplierEmail?.(record.email_address);
        }
      }
    });
  }, [pur_id]);

  if (!data) return null;

  const isIndividual = data.is_cal_type === "individual";
  // Previously gated on "isPharmacy" alone, so a non-pharmacy account whose
  // items happen to be batch-tracked never saw which batch a line was sold
  // from -- show the column whenever this voucher actually has batch data,
  // not just when the subscription type is Pharmacy.
  const showBatchColumns =
    (isPharmacy && authInfo?.enable_batch_number !== "no") ||
    (data.details || []).some((d) => d.batch_name);
  const items = (data.details || []).map(item => ({
    ...item,
    qty_display:
      item?.item_qty +
      " " +
      item?.unit_symbol +
      (item?.conversion > 1
        ? ", " + item?.retail_qty + " " + item?.base_unit_name
        : ""),
    // done_qty_display :
    //           item?.done_item_qty +
    //           " " +
    //           item?.unit_symbol +
    //           (item?.conversion > 1
    //             ? ", " + item?.done_retail_qty + " " + item?.base_unit_name
    //             : ""),
  }))

  const gpPercent = (retailRate, itemRate) => {
    const retail = Number(retailRate || 0);
    const rate = Number(itemRate || 0);
    if (!retail) return 0;
    return ((retail - rate) / retail) * 100;
  };

  // A product can appear as more than one line in the same voucher (e.g. a
  // paid line plus a separate zero-rate "Bonus" line for free stock). GP%
  // per raw line would then show the bonus line at a false 100% margin, so
  // blend the cost across all lines of the same item_id before comparing to
  // the retail rate.
  const effectiveRateByItem = items.reduce((acc, d) => {
    const key = d.item_id;
    const qty = Number(d.item_qty || 0);
    const rate = Number(d.item_rate || 0);
    const agg = acc[key] || { qty: 0, value: 0 };
    agg.qty += qty;
    agg.value += qty * rate;
    acc[key] = agg;
    return acc;
  }, {});

  const getEffectiveItemRate = (d) => {
    const agg = effectiveRateByItem[d.item_id];
    return agg && agg.qty > 0 ? agg.value / agg.qty : Number(d.item_rate || 0);
  };

  const showApiError = (err) => {
    if (err?.response?.data?.message) {
      Swal({
        title: err.response.data.message,
        icon: "warning",
      });
    } else {
      Swal({
        title: "Internal Error", text: "If the issue persists, please contact our support team.",
        icon: "warning"
      });
    }
  };

  const openRateModal = async (row) => {
    setEditingRow(row);
    setEditingItemMeta(null);
    setRateModalOpen(true);
    setRateModalLoading(true);
    try {
      const res = await getItemApi({ itemId: row.item_id });
      const productRow = Array.isArray(res?.data) ? res.data[0] : null;
      if (!productRow) throw new Error("Product not found");

      setEditingItemMeta(productRow);
      rateForm.setFieldsValue({
        retail_rate: Number(productRow.sale_rate ?? row.retail_rate ?? 0),
      });
    } catch (err) {
      setRateModalOpen(false);
      showApiError(err);
    } finally {
      setRateModalLoading(false);
    }
  };

  const closeRateModal = () => {
    setRateModalOpen(false);
    setEditingRow(null);
    setEditingItemMeta(null);
    rateForm.resetFields();
  };

  const handleSaveRetailRate = async () => {
    let values;
    try {
      values = await rateForm.validateFields();
    } catch {
      return; // inline validation errors already shown by the form
    }

    setRateModalSaving(true);
    try {
      await saveItemApi({
        action: "update",
        item_id: editingRow.item_id,
        item_name: editingItemMeta.item_name,
        opening_qty: editingItemMeta.opening_qty,
        opening_rate: editingItemMeta.opening_rate,
        sale_rate: values.retail_rate,
      });

      const targetPurDId = editingRow.pur_d_id;
      setData((prev) => ({
        ...prev,
        details: (prev.details || []).map((d) =>
          d.pur_d_id === targetPurDId
            ? { ...d, retail_rate: values.retail_rate }
            : d
        ),
      }));

      message.success("Retail rate updated successfully");
      closeRateModal();
    } catch (err) {
      showApiError(err);
    } finally {
      setRateModalSaving(false);
    }
  };

  const totals = items.reduce(
    (acc, d) => {
      const qty = d.item_qty || d.pur_o_qty || 0;
      const rate = Number(d.item_rate || 0);
      let base = qty * rate;

      const discountAmt =
        (Number(d.item_discount_per || 0) / 100) * base;

      const afterDiscount = base - discountAmt;

      const taxAmt =
        (Number(d.item_tax_per || 0) / 100) * afterDiscount;

      acc.subTotal += base;
      acc.discountTotal += discountAmt;
      acc.taxTotal += taxAmt;
      acc.grandTotal += afterDiscount + taxAmt;

      return acc;
    },
    { subTotal: 0, discountTotal: 0, taxTotal: 0, grandTotal: 0 }
  );

  const serviceCost = Number(data.other_service_cost || 0);
  // calculate grand total
  const grandTotal = isIndividual
    ? items.reduce((sum, d) => {
      let qty = d.item_qty || d.pur_o_qty || 0;
      let rate = Number(d.item_rate || 0);
      let total = qty * rate;
      total -= (Number(d.item_discount_per) / 100) * total;
      total += (Number(d.item_tax_per) / 100) * total;
      return sum + total;
    }, 0) + serviceCost
    : (data?.total_amount || 0) + serviceCost;


  const sellingPriceColumn = {
    title: "Selling Price",
    key: "retail_rate",
    align: "right",
    render: (_, d) => (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 6,
          width: "100%",
        }}
      >
        {Number(d.retail_rate || 0).toFixed(2)}
        {!printMode && (
          <EditOutlined
            style={{ cursor: "pointer", color: "#1677ff" }}
            onClick={() => openRateModal(d)}
          />
        )}
      </span>
    ),
  };

  const batchNoColumn = {
    title: "Batch No",
    dataIndex: "batch_name",
    align: "center",
    render: (v) => v || "-",
  };

  const expiryDateColumn = {
    title: "Expiry Date",
    dataIndex: "batch_expiry",
    align: "center",
    render: (v) => (v ? formatDateOnly(v) : "-"),
  };

  const gpColumn = {
    title: "GP%",
    key: "gp_percent",
    align: "right",
    render: (_, d) =>
      gpPercent(d.retail_rate, getEffectiveItemRate(d)).toFixed(2).replace(/\.00$/, ""),
  };

  const columns = isIndividual
    ? [
      { title: "NO.", render: (_, __, index) => index + 1, width: 40 },
      { title: "Item Name", dataIndex: "item_name", className: "col-item" },
      {
        title: "Rate",
        dataIndex: "item_rate",
        align: "right",
        render: (v) => Number(v).toFixed(2),
      },
      sellingPriceColumn,
      { title: "Per", dataIndex: "unit_name", align: "right" },
      {
        title: "Quantity",
        dataIndex: "item_qty",
        align: "right",
        render: (v) => Number(v).toFixed(2).replace(/\.00$/, "")
      },
      {
        title: "Discount%",
        dataIndex: "item_discount_per",
        align: "right",
        render: (v) => Number(v).toFixed(2).replace(/\.00$/, "")
      },
      ...(showBatchColumns ? [batchNoColumn, expiryDateColumn] : []),
      gpColumn,
      ...(!isGSTDisabled
        ? [
          {
            title: `${taxLabel}%`,
            dataIndex: "item_tax_per",
            align: "right",
            render: (v) => Number(v).toFixed(2).replace(/\.00$/, "")
          },
        ]
        : []),

      {
        title: "Total",
        align: "right",
        render: (_, d) => {
          let qty = d.item_qty || d.item_qty || 0;
          let rate = Number(d.item_rate || 0);
          let total = qty * rate;
          total -= (Number(d.item_discount_per) / 100) * total;
          total += (Number(d.item_tax_per) / 100) * total;
          return total.toFixed(2);
        },
      },
    ]
    : [
      { title: "NO.", render: (_, __, index) => index + 1, width: 40 },
      { title: "Item Name", dataIndex: "item_name", className: "col-item" },
      {
        title: "Rate",
        dataIndex: "item_rate",
        align: "right",
        render: (v) => Number(v).toFixed(2),
      },
      sellingPriceColumn,
      { title: "Per", dataIndex: "per_unit_symbol", align: "right" },
      { title: "Quantity", dataIndex: "qty_display", align: "right" },
      // { title: "Sold Quantity", dataIndex: "done_qty_display", align: "right" },
      ...(showBatchColumns ? [batchNoColumn, expiryDateColumn] : []),
      gpColumn,
      {
        title: "Amount",
        dataIndex: "item_total",
        align: "right",
        render: (v) => Number(v).toFixed(2),
      },
    ];

  const totalTax = isIndividual ? totals.taxTotal : Number(data?.total_tax || 0);


  return (
    <div className="voucher-section invoice-v2">
      {/* Header */}

      <Row justify="space-between" align="top" className="invoice-header">

        <Col flex="0 0 320px" className="bill-to" style={{ marginTop: "58px" }}>
          <Text strong>Supplier/ Creditor Code: &nbsp;{data.acc_code}</Text>
          <br />
          <Text strong>Name: &nbsp;{data.acc_name}</Text>
          <br />
          {/* <Text >Institution: &nbsp;{data.institution_name}</Text>
          <br /> */}
          <Text className="wrap-text">Address: &nbsp; {data.address}</Text>

          <Text>Contact No: &nbsp; +{data.contact_no}</Text>
        </Col>
        <Col className="invoice-title">
          <Title level={2}>PURCHASE INVOICE</Title>
          <Text strong>Voucher No: {data.pur_voucher_no}</Text>
          <br />
          {isTatmeenUAE && <>
            <Text>SSCC Code: {data.sscc_code}</Text>
          </>}
          <br />
          <Text>Expense By: {data.full_name?.split(" ")[0]}</Text>
          <br />
          <Text className="wrap-text">Address : {institution?.street_address}</Text>
          {institution?.phone_no && <Text>Contact No : &nbsp; +{institution?.phone_no}</Text>}
          <br />
          {institution?.vat_license && <><Text>
            {institution?.country?.trim().toLowerCase() === "united arab emirates"
              ? "TRN No: "
              : "Tax Reg No: "}{" "}
            {institution?.vat_license}
          </Text><br /></>}
          {data.licence_no && <Text>License No : {data.licence_no}</Text>}
        </Col>
      </Row>


      {/* Items Table */}
      <Table
        className="invoice-items"
        dataSource={items}
        columns={columns}
        pagination={false}
        rowKey="item_id"
        size="small"
        summary={() => {
          if (isIndividual) {
            return (
              <Table.Summary.Row>
                {/* <Table.Summary.Cell colSpan={5} align="right">
                  <b>Grand Total</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <b>{grandTotal.toFixed(2)}</b>
                </Table.Summary.Cell> */}
              </Table.Summary.Row>
            );
          }
          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={4} align="right">
                  Subtotal
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {Number(data.sub_total || 0).toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={4} align="right">
                  Discount Amount
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {Number(data.total_discount || 0).toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              {isIndiaGst ? (
                <>
                  {data.place_of_supply_type == 'intra' && isIndiaGst && data.is_cal_type == "individual" && <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={4} align="right">
                        CGST
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        {numberFormatTax(data.cgst_amount)}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>

                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={4} align="right">
                        SGST
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        {numberFormatTax(data.sgst_amount)}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>}

                  {data.place_of_supply_type == 'inter' && isIndiaGst && data.is_cal_type == "individual" && <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={4} align="right">
                        IGST
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        {numberFormatTax(data.igst_amount)}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>}

                  {data.gst_calculated == 'no' && <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={4} align="right">
                        {taxLabel}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        {totalTax.toFixed(2)}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>}

                </>
              ) : (
                !isIndia && (
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={4} align="right">
                      {taxLabel}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      {totalTax.toFixed(2)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                ))}

              {/* <Table.Summary.Row>
                <Table.Summary.Cell colSpan={3} align="right">
                  <b>Grand Total</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <b>{Number(grandTotal).toFixed(2)}</b>
                </Table.Summary.Cell>
              </Table.Summary.Row> */}
            </>
          );
        }}
      />

      {/* Footer */}
      <Divider />
      <Row>
        <Col span={16}>
          {/* <Text>Invoice Due : {numberFormat(data.due_total || 0)}</Text>
    <br />
    <Text>
      Current Customer Due : {numberFormat(data.due_amount || 0)}
    </Text> */}
          {/* 🔽 Payment Breakdown */}
          {!printMode && data?.trans && data.trans.length > 0 && (
            <Row gutter={[16, 8]} style={{ marginTop: 12 }}>
              <Col span={10}>
                <Divider orientation="left" plain style={{ margin: "8px 0" }}>
                  Payment Breakdown
                </Divider>
                <Table
                  size="small"
                  bordered
                  pagination={false}
                  style={{ width: "100%" }}  // ✅ take full col width
                  rowKey={(_, index) => index}
                  dataSource={data.trans.map((tran, index) => ({
                    key: index,
                    index: index + 1,
                    account: tran?.from_acc_name || "N/A",
                    amount: parseFloat(
                      data?.conversion_rate && data.conversion_rate !== 0
                        ? tran.tran_amount * data.conversion_rate
                        : tran.tran_amount
                    ).toFixed(2),
                  }))}
                  columns={[
                    {
                      title: "#",
                      dataIndex: "index",
                      width: 50,
                    },
                    {
                      title: "Cash / Account",
                      dataIndex: "account",
                      // ellipsis: true,
                      onCell: () => ({
                        style: { minWidth: 120 },   // ✅ enforce minWidth
                      }),
                    },
                    {
                      title: "Amount",
                      dataIndex: "amount",
                      align: "right",
                      width: 120,
                      render: (val) => numberFormat(val),
                    },
                  ]}

                />
              </Col>
              <Col span={2}></Col>
            </Row>
          )}
        </Col>

        <Col span={8} style={{ display: "flex", justifyContent: "flex-end" }}>

          <div>
            {/* Quantity */}
            {/* <div style={{ display: "flex", textAlign: "left" }}>
              <Text>Quantity Total :</Text>
              <Text>
                ({items.reduce((sum, d) => sum + (Number(d.item_qty) || 0), 0)})
              </Text>
            </div> */}

            {/* Sub Total */}
            {/* <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Text>Sub Total : </Text>
        <Text>{numberFormat(isIndividual ? grandTotal : data.sub_total || 0)}</Text>
      </div> */}

            <div className="invoice-totals">
              <div className="row">
                <span>Sub Total</span>
                <span>{numberFormat(isIndividual ? totals.subTotal : data.sub_total)}</span>
              </div>

              <div className="row">
                <span>Discount</span>
                <span>{numberFormat(isIndividual ? totals.discountTotal : data.total_discount)}</span>
              </div>

              {isIndiaGst ? (
                <>

                  {data.place_of_supply_type == 'intra' && isIndiaGst && <>
                    <div className="row">
                      <span>CGST</span>
                      <span>{numberFormat(data.cgst_amount)}</span>
                    </div>

                    <div className="row">
                      <span>SGST</span>
                      <span>{numberFormat(data.sgst_amount)}</span>
                    </div>
                  </>}

                  {data.place_of_supply_type == 'inter' && isIndiaGst && <>
                    <div className="row">
                      <span>IGST</span>
                      <span>{numberFormat(data.igst_amount)}</span>
                    </div>
                  </>}

                  {data.gst_calculated == 'no' && <>
                    <div className="row">
                      <span>Total {taxLabel}</span>
                      <span>{numberFormat(totalTax)}</span>
                    </div>
                  </>}

                </>
              ) : (
                !isIndia && (
                  <div className="row">
                    <span>Total {taxLabel}</span>
                    <span>{numberFormat(totalTax)}</span>
                  </div>
                ))}

              {Number(data.other_service_cost) > 0 && (
                <div className="row">
                  <span>Additional Expense</span>
                  <span>{numberFormat(data.other_service_cost)}</span>
                </div>
              )}



              <div className="grand">
                <span>Grand Total</span>
                <span>{numberFormat(grandTotal)}</span>
              </div>

              <div className="row">
                <Text strong>Paid : {institution?.currency}</Text>
                <Text strong>{numberFormat(data.paid_total || data.paid_amount || 0)}</Text>
              </div>

              <div className="row">
                <Text strong>Due : {institution?.currency}</Text>
                <Text strong>{numberFormat(data.due_total || data.due_amount || 0)}</Text>
              </div>
            </div>

            {/* Grand Total */}
            {/* <div
              style={{
                borderTop: "1px solid #000",
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 4,
                marginTop: 4,
              }}
            >
              <Text strong>Grand Total : {institution?.currency}</Text>
              <Text strong>{numberFormat(grandTotal)}</Text>
            </div> */}

            {/* Paid */}
            {/* <div
              style={{
                borderTop: "1px solid #000",
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 4,
                marginTop: 4,
              }}
            >
              <Text strong>Paid : {institution?.currency}</Text>
              <Text strong>{numberFormat(data.paid_total || data.paid_amount || 0)}</Text>
            </div> */}

            {/* Due */}
            {/* <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 2,
              }}
            >
              <Text strong>Due : {institution?.currency}</Text>
              <Text strong>{numberFormat(data.due_total || data.due_amount || 0)}</Text>
            </div> */}
          </div>
        </Col>
      </Row>

      <Divider />
      <Row>
        <Col span={24}>
          <Text>
            In Word of Grand Total :{" "}
            {convertNumberToWords(grandTotal, data?.currency || institution?.currency)}
          </Text>
          <br />
          <Text>Description : {data.narration || "-"}</Text>
        </Col>
      </Row>

      {(showStamp || showSignature) && (
        <div className="invoice-footer-section">
          <div className="footer-right-group">
            {showStamp && institution?.stamp && (
              <div className="stamp-block">
                <img src={institution.stamp} alt="Stamp" />
              </div>
            )}

            {showSignature && institution?.signature && (
              <div className="signature-block">
                <img src={institution.signature} alt="Signature" />
                <div className="sign-line-text">Authorized Signature</div>
              </div>
            )}
          </div>
        </div>
      )}

      {!printMode && (
        <Modal
          title="Edit Retail Rate"
          open={rateModalOpen}
          onCancel={closeRateModal}
          onOk={handleSaveRetailRate}
          confirmLoading={rateModalSaving}
          okButtonProps={{ disabled: rateModalLoading }}
          destroyOnClose
        >
          <Form form={rateForm} layout="vertical" disabled={rateModalLoading}>
            <Form.Item label="Item">
              <Text>{editingRow?.item_name}</Text>
            </Form.Item>
            <Form.Item
              label="Retail Rate"
              name="retail_rate"
              rules={[{ required: true, message: "Retail Rate is required" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default PurchaseVoucherBody;
